import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { Button, Input } from "reactstrap";
import notifications from "../../common/Components/Notifications/Notification";
import { canUseRounds, isKarkhanaSupervisor } from "../../helpers/karkhanaEnabled";
import { listSarees, sareeProduct, searchStudioProducts, setSareePublic, unlinkSareeProduct } from "../../services/karkhanaApi";
import "../gi-registry/gi-registry.scss";
import "./ui/karkhana-history.scss";
import { fmtDay } from "./rates/rateModel";
import "./workers/karkhana-workers.scss";
import { useKT } from "./i18n/kt";
import PageHeader from "./ui/PageHeader";
import SearchField from "./ui/SearchField";
import { confirmDialog } from "./ui/dialog";
import SareeLabelAttach from "./sarees/SareeLabelAttach";
import { kp } from "./karkhanaPaths";
import { useAddressParam } from "./ui/addressState";
import { OutLink } from "./ui/leaveKarkhana";

const readRole = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users?.role;
  } catch {
    return undefined;
  }
};

const STATUS: Record<string, string> = { ACTIVE: "On the loom", COMPLETED: "Completed", STOPPED: "Stopped", CUT: "Cut" };

/**
 * Every saree number with its HKP product (K24): make a draft product from the saree, or link one the
 * studio already has. The product page then shows the saree's production history.
 */
const KarkhanaSareesPage = () => {
  const { t } = useKT();
  const history = useHistory();
  const role = readRole();
  const staff = !isKarkhanaSupervisor(role);
  // Search and status are in the address (replaced while typing), so Back from a saree or product keeps them.
  const [q, setQ] = useAddressParam("q");
  const [status, setStatus] = useAddressParam("status");
  const [rows, setRows] = useState<any[] | null>(null);
  const [busy, setBusy] = useState("");
  const [linking, setLinking] = useState<string | null>(null);
  const [found, setFound] = useState<any[]>([]);
  const [term, setTerm] = useState("");
  const timer = useRef<number | undefined>(undefined);

  const load = useCallback(async () => {
    try {
      const res: any = await listSarees(q.trim() || undefined, status || undefined);
      setRows(res?.success ? res.data || [] : []);
    } catch {
      setRows([]);
    }
  }, [q, status]);

  useEffect(() => {
    if (!canUseRounds(role)) {
      history.replace(kp.home());
      return;
    }
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(load, 250);
    return () => window.clearTimeout(timer.current);
  }, [role, history, load]);

  useEffect(() => {
    if (!linking || term.trim().length < 2) {
      setFound([]);
      return;
    }
    const t = window.setTimeout(async () => {
      const res: any = await searchStudioProducts(term.trim()).catch(() => null);
      const list = res?.data?.docs || res?.data?.products || res?.data || [];
      setFound(Array.isArray(list) ? list.slice(0, 8) : []);
    }, 250);
    return () => window.clearTimeout(t);
  }, [term, linking]);

  const act = async (id: string, fn: () => Promise<any>, ok: string) => {
    setBusy(id);
    try {
      const res: any = await fn();
      if (!res?.success) {
        notifications({ title: t("Error"), message: t(res?.message || "Could not save"), type: "danger" });
        return;
      }
      notifications({ title: t("Done"), message: ok, type: "success" });
      setLinking(null);
      setTerm("");
      load();
    } catch {
      notifications({ title: t("Error"), message: t("Could not reach the server"), type: "danger" });
    } finally {
      setBusy("");
    }
  };

  if (!canUseRounds(role)) return null;

  return (
    <div className="gi-registry-page kw-page">
      <PageHeader title={t("Sarees")} back={isKarkhanaSupervisor(role) ? kp.rounds() : kp.more()} backLabel={t("Back")} />
      <p className="kw-lede">
        {t("Each saree number becomes one HKP product. Its loom work shows on the product page as production history, never with wages.")}
        {staff ? " You can also show a short “woven on our loom” summary on the public product page." : ""}
      </p>
      <SearchField value={q} onChange={setQ} placeholder={t("Saree number, design or loom")} label={t("Search sarees")}>
        <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
          <option value="">{t("All")}</option>
          {Object.entries(STATUS).map(([k, l]) => (
            <option key={k} value={k}>
              {l}
            </option>
          ))}
        </select>
      </SearchField>

      {!rows ? <div className="kw-empty">{t("Loading…")}</div> : null}
      {rows && !rows.length ? <div className="kw-empty">{t("No sarees yet. They appear here when started on a loom.")}</div> : null}
      {(rows || []).map((s) => (
        <div key={s.id} className="kw-card">
          <div className="kr-pay__row">
            <Link to={kp.saree(s.id)} className="kh-saree-link">
              <span className="kw-code">{s.sareeNumber}</span> · {s.designNumber} · {t("loom")} {s.loomCode} <span aria-hidden="true">›</span>
            </Link>
            <span className={`kw-chip${s.status === "ACTIVE" ? " is-info" : s.status === "COMPLETED" ? " is-ok" : ""}`}>{t(STATUS[s.status] || s.status)}</span>
          </div>
          <div className="kw-muted">
            {s.startedOn ? t("Started {{day}}", { day: fmtDay(s.startedOn) }) : ""}
            {s.finishedOn ? ` · ${t("finished {{day}}", { day: fmtDay(s.finishedOn) })}` : ""} · {t("{{m}} m entered", { m: s.enteredMetres })}
          </div>
          <div className="kw-toolbar" style={{ marginTop: 8, marginBottom: 0, flexWrap: "wrap" }}>
            {s.status === "ACTIVE" || s.status === "COMPLETED" || s.labelSerial ? (
              <SareeLabelAttach pieceId={s.id} sareeNumber={s.sareeNumber} labelSerial={s.labelSerial} onAttached={() => load()} />
            ) : null}
            {s.product ? (
              <>
                <OutLink to={`/product/${s.product.productUniqueId}`} className="btn btn-sm btn-hastkala-outline">
                  Product: {s.product.name || s.product.productUniqueId}
                  {s.product.status === "DRAFTED" ? " (draft)" : ""}
                </OutLink>
                {staff ? (
                  <>
                    <label className="kw-muted" style={{ display: "flex", gap: 6, alignItems: "center", margin: 0 }}>
                      <input
                        type="checkbox"
                        checked={!!s.productionPublic}
                        disabled={busy === s.id}
                        onChange={(e) =>
                          act(s.id, () => setSareePublic(s.id, e.target.checked), e.target.checked ? t("Shown on the public page") : t("Hidden from the public page"))
                        }
                      />
                      {t("Show on public page")}
                    </label>
                    <Button size="sm" outline disabled={busy === s.id} onClick={async () => (await confirmDialog({ title: `Unlink the product from saree ${s.sareeNumber}?` })) && act(s.id, () => unlinkSareeProduct(s.id), "Unlinked")}>
                      {t("Unlink")}
                    </Button>
                  </>
                ) : null}
              </>
            ) : staff ? (
              <>
                <Button size="sm" className="ResetBtn" disabled={busy === s.id} onClick={() => act(s.id, () => sareeProduct(s.id), `Draft product made for saree ${s.sareeNumber}`)}>
                  {t("Create product draft")}
                </Button>
                <Button size="sm" outline disabled={busy === s.id} onClick={() => { setLinking(linking === s.id ? null : s.id); setTerm(""); }}>
                  {t("Link existing product")}
                </Button>
              </>
            ) : (
              <span className="kw-muted">{t("No product yet")}</span>
            )}
          </div>
          {linking === s.id ? (
            <div style={{ marginTop: 8 }}>
              <Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder={t("Search your products by name")} aria-label="Search products" autoFocus />
              {found.map((p: any) => (
                <button
                  key={p.productUniqueId}
                  type="button"
                  className="btn btn-link btn-sm d-block p-0 mt-1 text-start"
                  onClick={() => act(s.id, () => sareeProduct(s.id, p.productUniqueId), `Saree ${s.sareeNumber} linked`)}
                >
                  {p.name || p.productUniqueId}
                  {p.srNo ? ` · ${p.srNo}` : ""}
                </button>
              ))}
              {term.trim().length >= 2 && !found.length ? <div className="kw-muted mt-1">{t("No matching products.")}</div> : null}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
};

export default KarkhanaSareesPage;
